import { ref, readonly } from 'vue';
import axios from '@/plugins/axios';
import Socket from '@/services/websocket';
import { useToast } from '@/composables/use-toast';
import { useStorage, isDuplicateModelRunQueue } from '@/composables/use-storage';
import notificationSoundUrl from '@/sounds/new-notification.mp3';
import logo from '@/assets/images/avatar/orb_avatar.png';

const dedupeModelRunQueueNotifications = (list) => {
  const result = [];
  for (const item of list) {
    if (!isDuplicateModelRunQueue(result, item)) {
      result.push(item);
    }
  }
  return result;
};

const notifications = ref([]);
const isLoading = ref(false);
const unreadCount = ref(0);

let socketInstance = null;
let newNotificationCallbacks = [];

export function useNotifications() {

  const { addToast } = useToast();
  const {
    getUserInfo,
    getNotifications,
    saveNotifications,
    isNotificationsCacheValid,
    clearNotificationsCache,
    updateNotificationInCache,
    addNotificationToCache,
    removeNotificationFromCache,
    setSoundEnabled,
    getSoundEnabled,
  } = useStorage();
  
  const userInfo = getUserInfo();
  
  // load from cache
  const loadFromCache = () => {
    try {
      const cached = getNotifications();
      if (cached?.notifications) {
        notifications.value = dedupeModelRunQueueNotifications(
          Array.isArray(cached.notifications) ? cached.notifications : [],
        );
        unreadCount.value = cached.unreadCount || 0;
        return true;
      }
    } catch (error) {
      console.error('Error loading notifications from cache:', error);
    }
    
    if (!Array.isArray(notifications.value)) {
      notifications.value = [];
    }
    if (typeof unreadCount.value !== 'number') {
      unreadCount.value = 0;
    }
    
    return false;
  };

  // save to cache
  const saveToCache = () => {
    return saveNotifications(notifications.value, unreadCount.value);
  };

  // fetch notifications (with cache)
  const fetchNotifications = async (forceRefresh = false) => {
    // if not force refresh and cache is valid, use cache
    if (!forceRefresh && isNotificationsCacheValid() && notifications.value.length > 0) {
      return notifications.value;
    }

    // try to load from cache first
    if (!forceRefresh && loadFromCache()) {
      // cache loaded, fetch in background to update
      fetchNotificationsFromAPI(false);
      return notifications.value;
    }

    // fetch from API
    return fetchNotificationsFromAPI(true);
  };

  // fetch from API
  const fetchNotificationsFromAPI = async (showLoading = true) => {
    if (showLoading) isLoading.value = true;
    
    try {
      const response = await axios.get('/api/notifications');
      const data = response.data.data;
      const fetched = Array.isArray(data.notifications) ? data.notifications : [];
      notifications.value = dedupeModelRunQueueNotifications(fetched);
      unreadCount.value = notifications.value.filter(notification => !notification.readed).length || 0;
      
      // save to cache
      saveToCache();
      
      return notifications.value;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      
      // if failed and no cache, try to load expired cache
      if (!Array.isArray(notifications.value) || notifications.value.length === 0) {
        loadFromCache();
      }
      
      if (!Array.isArray(notifications.value)) {
        notifications.value = [];
      }
    } finally {
      if (showLoading) isLoading.value = false;
    }
  };

  // mark as read
  const markAsRead = async (notificationIds) => {
    try {
      await axios.post(`/api/notifications/read`, notificationIds);
      
      if (!Array.isArray(notifications.value)) {
        notifications.value = [];
      }
      
      // update local state
      notificationIds.forEach(id => {
        const index = notifications.value.findIndex(n => n.id === id);
        if (index > -1 && !notifications.value[index].readed) {
          notifications.value[index].readed = true;
          unreadCount.value = Math.max(0, unreadCount.value - 1);
        }
      });
      
      // update cache using storage
      notificationIds.forEach(id => {
        updateNotificationInCache(id, { readed: true });
      });
      
      saveToCache();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // delete notification
  const deleteNotification = async () => {
    try {
      await axios.post(`/api/notifications/delete-all`, {
        user_id: userInfo.id
      });
      
      if (!Array.isArray(notifications.value)) {
        notifications.value = [];
      }
      
      notifications.value.forEach(notification => {
        if (!notification.readed) {
          unreadCount.value = Math.max(0, unreadCount.value - 1);
        }
        removeNotificationFromCache(notification.id);
      });
      
      notifications.value = [];
      
      saveToCache();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // add new notification (via WebSocket)
  const handleNewNotification = (notificationData) => {
    if (!Array.isArray(notifications.value)) {
      notifications.value = [];
    }

    const existsById = notifications.value.find(n => n.id === notificationData.id);
    const isDuplicateQueue = isDuplicateModelRunQueue(notifications.value, notificationData);
    if (!existsById && !isDuplicateQueue) {
      notifications.value.unshift(notificationData);
      unreadCount.value += 1;
      
      // limit notifications in memory
      const MAX_CACHED_NOTIFICATIONS = 50;
      if (notifications.value.length > MAX_CACHED_NOTIFICATIONS) {
        notifications.value = notifications.value.slice(0, MAX_CACHED_NOTIFICATIONS);
      }
      
      // add to cache using storage
      addNotificationToCache(notificationData);
      
      if (notificationData.type === 'file.schema_processed') {
        const { setIsFileProcessingValue } = useStorage();
        setIsFileProcessingValue(false);
        
        addToast('File processing complete!', 'success');
      } else if (notificationData.type !== 'model_run.queue') {
        addToast('New notification received!', 'info');
      }
      
      playNotificationSound();
      showDesktopNotification(notificationData);
      
      newNotificationCallbacks.forEach(callback => {
        try {
          callback(notificationData);
        } catch (error) {
          console.error('Error calling new notification callback:', error);
        }
      });
    }
  };

  const onNewNotificationReceived = (callback) => {
    if (typeof callback === 'function') {
      newNotificationCallbacks.push(callback);
    }
  };

  const offNewNotificationReceived = (callback) => {
    const index = newNotificationCallbacks.indexOf(callback);
    if (index > -1) {
      newNotificationCallbacks.splice(index, 1);
    }
  };

  const showDesktopNotification = (notificationData) => {
    const template = getNotificationTemplate(notificationData);
    if (Notification.permission === 'granted') {
      const notification = new Notification(template?.title ?? 'New notification', {
        body: template?.message ?? 'You have a new notification',
        icon: logo,
        requireInteraction: false,
      });

      // Handle click event
      notification.onclick = () => {
        window.focus();
        window.location.href = template?.link ?? '/explore';
        notification.close();
      };

      setTimeout(() => {
        notification.close();
      }, 8000);
    }
  }

  // update notification (via WebSocket)
  const handleNotificationUpdated = (updatedNotification) => {
    if (!Array.isArray(notifications.value)) {
      notifications.value = [];
    }
    
    const index = notifications.value.findIndex(n => n.id === updatedNotification.id);
    if (index > -1) {
      const oldNotification = notifications.value[index];
      notifications.value[index] = updatedNotification;
      
      // update unread count if read status changed
      if (!oldNotification.readed && updatedNotification.readed) {
        unreadCount.value = Math.max(0, unreadCount.value - 1);
      }
      
      // update cache using storage
      updateNotificationInCache(updatedNotification.id, updatedNotification);
    }
  };

  // delete notification (via WebSocket)
  const handleNotificationDeleted = (deletedNotification) => {
    if (!Array.isArray(notifications.value)) {
      notifications.value = [];
    }
    
    const index = notifications.value.findIndex(n => n.id === deletedNotification.id);
    if (index > -1) {
      if (!notifications.value[index].readed) {
        unreadCount.value = Math.max(0, unreadCount.value - 1);
      }
      notifications.value.splice(index, 1);
    }
  };

  // watch for synchronization between tabs (using storage event)
  const handleStorageChange = (event) => {
    const userCacheKey = `notifications_${userInfo?.id}`;
    if (event.key === userCacheKey && event.newValue) {
      try {
        const data = JSON.parse(event.newValue);
        const cached = getNotifications();
        if (cached && data.timestamp > cached.timestamp) {
          notifications.value = Array.isArray(data.notifications) ? data.notifications : [];
          unreadCount.value = data.unreadCount || 0;
        }
      } catch (error) {
        console.error('Error synchronizing notifications between tabs:', error);
        if (!Array.isArray(notifications.value)) {
          notifications.value = [];
        }
      }
    }
  };

  const handleTaskBundlesDownloaded = (datasetId) => {
    try {
        addToast('Task bundles downloaded!', 'info');
        socketInstance.sendTaskBundlesDownloaded(datasetId);
    } catch (error) {
      console.error('Error handling task bundles downloaded:', error);
    }
  };

  const getNotificationTemplate = (notification) => {
    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    }
    const metadata = notification.metadata;
    const templates = {
      // Bids template
        'bid.accepted': {
            title: 'Bid accepted',
            message: `Your bid on ${metadata.dataset_name} was accepted by ${metadata.owner_username}.`,
            created_at: formatDate(notification.created_at),
            link: `/creator-profile?page=bids`,
        },
        'bid.rejected': {
            title: 'Bid rejected',
            message: `Your bid on ${metadata.dataset_name} was rejected by ${metadata.owner_username}.`,
            created_at: formatDate(notification.created_at),
            link: `/creator-profile?page=bids`,
        },
        'bid.credentials_ready_for_payment': {
            title: 'Credentials ready—complete purchase	',
            message: `Credentials for ${metadata.listing_name} are verified. Complete payment to reveal them (one-time view).`,
            created_at: formatDate(notification.created_at),
            link: `/creator-profile?page=bids&subpage=bids`,
        },
      // Offers template
        'offer.new': {
            title: 'New offer',
            message: `You received a new offer of ${metadata.amount} ${metadata.currency} on ${metadata.dataset_name} from ${metadata.username}.`,
            created_at: formatDate(notification.created_at),
            link: `/creator-profile?page=offers&subpage=offers`,
        },
      // Sales template
        'sale.new': {
            title: 'New sale',
            message: `Your dataset ${metadata.dataset_name} just sold to ${metadata.buyer_name} for ${metadata.price} ${metadata.currency}.`,
            created_at: formatDate(notification.created_at),
            link: `/creator-profile?page=sales`,
        },
        'sale.request_credentials': {
            title: 'Request credentials',
            message: `${metadata.buyer_name} requested credentials for ${metadata.listing_name}. Review and respond.`,
            created_at: formatDate(notification.created_at),
            link: `/creator-profile?page=sales`,
        },
        'sale.review_credentials': {
            title: 'Review credentials',
            message: `New credentials submitted for ${metadata.listing_name} by ${metadata.seller_name}. Verify access and mark as Verified or Needs Context.`,
            created_at: formatDate(notification.created_at),
            link: `/backoffice/sales/credentials`,
        },
        'sale.credentials_verified': {
            title: 'Credentials verified',
            message: `Credentials for ${metadata.listing_name} were verified successfully. The buyer can now complete payment.`,
            created_at: formatDate(notification.created_at),
            link: `/creator-profile?page=sales`,
        },
        'sale.credentials_needs_context': {
            title: 'Action needed: update credentials',
            message: `We couldn’t verify credentials for ${metadata.listing_name}. Please add missing context and resubmit.`,
            created_at: formatDate(notification.created_at),
            link: `/creator-profile?page=sales`,
        },
        'sale.credentials_ready_for_payment': {
            title: 'Credentials ready—complete purchase	',
            message: `Credentials for ${metadata.listing_name} are verified. Complete payment to reveal them (one-time view).`,
            created_at: formatDate(notification.created_at),
            link: `/creator-profile?page=purchases`,
        },
        'sale.credentials_rejected': {
            title: 'Credentials rejected',
            message: `Credentials for ${metadata.listing_name} were rejected.`,
            created_at: formatDate(notification.created_at),
            link: `/creator-profile?page=sales`,
        },
      // RFPs template
        'rfp.expired': {
            title: 'Your RFP has expired',
            message: `Your RFP ${metadata.rfp_title} has expired.`,
            created_at: formatDate(notification.created_at),
            link: `/creator-profile?page=rfp&subpage=edit-rfps`,
        },
        'rfp.new_request': {
            title: 'New RFP request',
            message: `The ${metadata.rpf_owner}'s RFP ${metadata.rfp_title} has a new request from ${metadata.username}.`,
            created_at: formatDate(notification.created_at),
            link: `/backoffice/rfps-arbitration`,
        },
        'rfp.new_response': {
            title: 'New RFP response',
            message: `Your RFP ${metadata.rfp_title} has a new response from ${metadata.username}.`,
            created_at: formatDate(notification.created_at),
            link: `/creator-profile?page=rfp`,
        },
        'rfp.new_application': {
            title: 'New application',
            message: `${metadata.seller} applied to RFP ${metadata.rfp_title}.`,
            created_at: formatDate(notification.created_at),
            link: `/creator-profile?page=new-proposal&proposalId=${metadata.proposal_id}`,
        },
        'rfp.application_approved': {
            title: 'Proposal accepted by RFP issuer	',
            message: `You’re approved to work on ${metadata.rfp_title}. Reward locked: ${metadata.amount} ${metadata.currency}${metadata.reserved_until ? '.Due by ' + metadata.reserved_until : ''}. When you are ready to submit your dataset, go to the listing and click "Respond to RFP.`,
            created_at: formatDate(notification.created_at),
            link: `/creator-profile?page=rfp-proposals-list`,
        },
        'rfp.application_rejected': {
            title: 'Proposal rejected by RFP issuer',
            message: `Your proposal for ${metadata.rfp_title} was not accepted. You can submit another proposal if you’d like`,
            created_at: formatDate(notification.created_at),
            link: `/creator-profile?page=rfp-proposals-list`,
        },
        'rfp.application_rejected_other': {
            title: 'Proposal rejected by RFP issuer',
            message: `Your proposal for ${metadata.rfp_title} was not accepted.`,
            created_at: formatDate(notification.created_at),
            link: `/creator-profile?page=rfp-proposals-list`,
        },
        'rfp.application_withdrawn': {
            title: 'Seller withdrawn',
            message: `The seller for ${metadata.rfp_title} has withdrawn. You can now evaluate other proposals and accept another seller to work on your RFP.`,
            created_at: formatDate(notification.created_at),
            link: `/creator-profile?page=rfp-proposals-list`,
        },
        'rfp.application_expired': {
            title: 'RFP expired',
            message: `The RFP ${metadata.rfp_title} has expired. No further actions can be taken.`,
            created_at: formatDate(notification.created_at),
            link: `/creator-profile?page=rfp-proposals-list`,
        },
      // Purchases template
        'purchase.complete': {
            title: 'Purchase complete',
            message: `Your purchase of ${metadata.dataset_name} is complete — access is now available.`,
            created_at: formatDate(notification.created_at),
            link: `/creator-profile?page=purchases`,
        },
      // Downloads template
        'download.finish': {
            title: 'Download complete',
            message: `Your download of ${metadata.dataset_name} finished successfully.`,
            created_at: formatDate(notification.created_at),
            link: null,
        },
        //Permission template
        'new-permission-rfp':{
          title: 'New permission',
          message: `You now have access to RFPs. Please sign-in again to access new features.`,
          created_at: formatDate(notification.created_at),
          link: null
        },
        'new-permission-dataset':{
          title: 'New permission',
          message: `You now have access to selling datasets. Please sign-in again to access new features.`,
          created_at: formatDate(notification.created_at),
          link: null
        },
        // credits template
        'credits.add': {
          title: 'Credits received',
          message: `You received ${metadata.amount} credits to purchase datasets`,
          created_at: formatDate(notification.created_at),
          link: '/creator-profile'  
        },
        'credits.used': {
          title: 'Credits used',
          message: `You used ${metadata.amount} credits. Now you have ${metadata.credits_balance} credits.`,
          created_at: formatDate(notification.created_at),
          link: '/creator-profile'
        },
        // change group template
        'change_group.new_request': {
          title: 'Change group request',
          message: `User ${metadata.username} sent a group change request.`,
          created_at: formatDate(notification.created_at),
          link: '/backoffice/groups'
        },
        // files template
        'file.schema_processed': {
          title: 'File schema processed',
          message: `Your file ${metadata.file_name} schema has been processed and soon will be available to edit.`,
          created_at: formatDate(notification.created_at),
          link: (window.location.href.includes('dataset-listing-create') || window.location.href.includes('listing-edit')) ? null : '/creator-profile'
        },
        // eval template
        'eval.created': {
          title: 'Eval created',
          message: `Your eval to ${metadata.dataset_name} was created successfully.`,
          created_at: formatDate(notification.created_at),
          link: null,
        },
        'eval.running': {
          title: 'Eval running',
          message: `Your eval to ${metadata.dataset_name} is running.`,
          created_at: formatDate(notification.created_at),
          link: null,
        },
        'eval.checkout': {
          title: 'Eval checkout',
          message: `Eval Run checkout successfully completed for ${metadata.dataset_name}.`,
          created_at: formatDate(notification.created_at),
          link: null,
        },
        'eval.report': {
          title: 'Eval report',
          message: `Your eval report to ${metadata.dataset_name} is ready to view!`,
          created_at: formatDate(notification.created_at),
          link: `/creator-profile?page=eval-results&subpage=eval-results`,
        },
        'task_bundles_downloaded': {
          title: 'Task bundles downloaded',
          message: `Your task bundles for ${metadata.dataset_name} have been downloaded.`,
          created_at: formatDate(notification.created_at),
          link: null,
        },
        // tags import templates
        'tags.import.complete': {
          title: 'Tags import complete',
          message: metadata.message || `Tags import completed successfully. Created ${metadata.created_tag_types || 0} tag types and ${metadata.created_tags || 0} tags from ${metadata.total_rows || 0} rows.`,
          created_at: formatDate(notification.created_at),
          link: '/backoffice/listings-tags',
        },
        'tags.import.failed': {
          title: 'Tags import failed',
          message: `Tags import failed: ${metadata.error || 'Unknown error'}. Please check the CSV file and try again.`,
          created_at: formatDate(notification.created_at),
          link: '/backoffice/listings-tags',
        },
        // listing templates
        'listing.open_source_trigger_failed': {
          title: 'Open source trigger failed',
          message: metadata.message || `Failed to trigger open source for listing ${metadata.listing_name || metadata.listing_id}.`,
          created_at: formatDate(notification.created_at),
          link: metadata.listing_link || (metadata.listing_id ? `/listing/${metadata.listing_id}` : '/creator-profile'),
        },
        // model-run (benchmark credits)
        'model_run.queue': {
          title: 'Model run queued',
          message:
            metadata.message ||
            `Your model run is #${metadata.queue_position ?? '—'} in the queue.`,
          created_at: formatDate(notification.created_at),
          link: `/creator-profile?page=eval-results&subpage=eval-results`,
        },
        'model_run.report': {
          title: 'Model run completed',
          message:
            metadata.status === 'failed'
              ? `Model run ${metadata.eval_run_id || ''} failed.`
              : `Model run finished${metadata.cost != null ? ` (cost ≈ ${metadata.cost})` : ''}.`,
          created_at: formatDate(notification.created_at),
          link: `/creator-profile?page=eval-results&subpage=eval-results`,
        },
        // publication review
        'publication.new_request': {
          title: 'New publication request',
          message: `Listing "${metadata.listing_name || metadata.listing_id}" is waiting for review.`,
          created_at: formatDate(notification.created_at),
          link: `/backoffice/publication-requests`,
        },
        'publication.approved': {
          title: 'Publication approved',
          message: `Your listing "${metadata.listing_name || metadata.listing_id}" is now live.`,
          created_at: formatDate(notification.created_at),
          link: metadata.listing_id ? `/listing/${metadata.listing_id}` : '/creator-profile',
        },
        'publication.rejected': {
          title: 'Publication rejected',
          message: `Your listing "${metadata.listing_name || metadata.listing_id}" was not approved. It remains private.`,
          created_at: formatDate(notification.created_at),
          link: '/creator-profile',
        },
    }
    return templates[notification.type];
  }

  const soundEnabled = ref(true);
  const playNotificationSound = () => {
      if (!soundEnabled.value) return;
      try {
          const audio = new Audio(notificationSoundUrl);
          audio.volume = 0.5;
          audio.play().catch(error => console.log('Error playing audio:', error));
      } catch (error) {
          console.log('Error playing notification sound:', error);
      }
  };

  const toggleSound = () => {
      soundEnabled.value = !soundEnabled.value;
      setSoundEnabled(soundEnabled.value);
      if (soundEnabled.value) playNotificationSound();
  }

  // Initialize WebSocket connection
  const initializeWebSocket = () => {
    if (!userInfo) {
      console.warn('Cannot initialize WebSocket: user not logged in');
      return null;
    }

    if (!socketInstance) {
      // Create socket with handlers
      const handlers = {
        handleNewNotification,
        handleNotificationUpdated,
        handleNotificationDeleted
      };

      socketInstance = new Socket(handlers);
    }

    // Connect with user info
    socketInstance.connect(userInfo);
    
    return socketInstance;
  };

  // Disconnect WebSocket
  const disconnectWebSocket = () => {
    if (socketInstance) {
      socketInstance.disconnect();
      socketInstance = null;
    }
  };

  // initialization
  const initialize = () => {
    if (!Array.isArray(notifications.value)) {
      notifications.value = [];
    }
    if (typeof unreadCount.value !== 'number') {
      unreadCount.value = 0;
    }
    
    // load from cache on initialization
    loadFromCache();
    
    // Initialize WebSocket connection
    initializeWebSocket();
    
    // listener for synchronization between tabs
    window.addEventListener('storage', handleStorageChange);

    // load sound enabled
    soundEnabled.value = getSoundEnabled();
    
    // cleanup
    const cleanup = () => {
      window.removeEventListener('storage', handleStorageChange);
      disconnectWebSocket();
    };
    
    return cleanup;
  };

  return {
    // readonly state
    notifications: readonly(notifications),
    unreadCount: readonly(unreadCount),
    isLoading: readonly(isLoading),
    soundEnabled: readonly(soundEnabled),
    
    // methods
    fetchNotifications,
    markAsRead,
    deleteNotification,
    handleNewNotification,
    handleNotificationUpdated,
    handleNotificationDeleted,
    clearCache: clearNotificationsCache,
    initialize,
    getNotificationTemplate,
    playNotificationSound,
    toggleSound,
    initializeWebSocket,
    disconnectWebSocket,
    onNewNotificationReceived,
    offNewNotificationReceived,
    handleTaskBundlesDownloaded,
    // helpers
    isCacheValid: isNotificationsCacheValid,
    refreshNotifications: () => fetchNotifications(true),
  };
}